import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAiVisCssVars } from './aivisTheme';
import './DataStudioDocsPage.css';

const CONNECTOR_URL = process.env.REACT_APP_LOOKER_STUDIO_CONNECTOR_URL || '';

const sections = [
  ['overview', 'How it works'],
  ['create-key', 'Create your key'],
  ['connect', 'Connect Looker Studio'],
  ['multiple-clients', 'Add more clients'],
  ['charts', 'Build the three charts'],
  ['dates-refresh', 'Dates and refreshes'],
  ['troubleshooting', 'Troubleshooting'],
];

function ConnectorIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="3" width="7" height="7" rx="2" />
      <rect x="14" y="3" width="7" height="7" rx="2" />
      <rect x="3" y="14" width="7" height="7" rx="2" />
      <rect x="14" y="14" width="7" height="7" rx="2" />
      <path d="M10 6.5h4M6.5 10v4M17.5 10v4M10 17.5h4" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 3h7v7" />
      <path d="M10 14 21 3" />
      <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m5 12 4 4L19 6" />
    </svg>
  );
}

function StepList({ children }) {
  return <ol className="aivis-docs-steps">{children}</ol>;
}

function Step({ title, children }) {
  return (
    <li>
      <div>
        <h3>{title}</h3>
        <div className="aivis-docs-step-copy">{children}</div>
      </div>
    </li>
  );
}

function Field({ label, value, note }) {
  return (
    <div className="aivis-docs-field-row">
      <dt>{label}</dt>
      <dd>
        <strong>{value}</strong>
        {note && <span>{note}</span>}
      </dd>
    </div>
  );
}

function Recipe({ number, title, description, children }) {
  return (
    <article className="aivis-docs-recipe">
      <header>
        <span>{number}</span>
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
      </header>
      <dl>{children}</dl>
    </article>
  );
}

export default function DataStudioDocsPage() {
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'AI-Vis Looker Studio Connector Guide';
    return () => { document.title = previousTitle; };
  }, []);

  useEffect(() => {
    let frameId = null;

    function updateActiveSection() {
      frameId = null;
      const marker = window.scrollY + Math.min(220, window.innerHeight * 0.3);
      let current = sections[0][0];

      sections.forEach(([id]) => {
        const section = document.getElementById(id);
        if (section && section.offsetTop <= marker) current = id;
      });
      setActiveSection(current);
    }

    function requestUpdate() {
      if (frameId === null) {
        frameId = window.requestAnimationFrame(updateActiveSection);
      }
    }

    updateActiveSection();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, []);

  return (
    <div className="aivis-docs-page" style={getAiVisCssVars()}>
      <header className="aivis-docs-topbar">
        <div className="aivis-docs-container aivis-docs-topbar-inner">
          <Link className="aivis-docs-brand" to="/ai-vis/home">
            <span className="aivis-docs-brand-mark"><ConnectorIcon size={18} /></span>
            <span>AI-Vis</span>
            <span className="aivis-docs-brand-divider" />
            <span className="aivis-docs-brand-section">Documentation</span>
          </Link>
          <div className="aivis-docs-topbar-actions">
            <Link className="aivis-docs-text-link" to="/ai-vis/settings">
              AI-Vis settings
            </Link>
            {CONNECTOR_URL && (
              <a
                className="aivis-docs-button aivis-docs-button--small"
                href={CONNECTOR_URL}
                target="_blank"
                rel="noreferrer noopener"
              >
                Open connector
                <ExternalLinkIcon />
              </a>
            )}
          </div>
        </div>
      </header>

      <main>
        <section className="aivis-docs-hero">
          <div className="aivis-docs-container">
            <p className="aivis-docs-eyebrow">Looker Studio connector</p>
            <h1>Turn AI visibility data into client-ready reports</h1>
            <p className="aivis-docs-lead">
              Connect AI-Vis once, choose a client for each data source, and build
              brand visibility and citation reporting in Looker Studio.
            </p>
            <div className="aivis-docs-hero-actions">
              {CONNECTOR_URL ? (
                <a
                  className="aivis-docs-button"
                  href={CONNECTOR_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Open Looker Studio connector
                  <ArrowIcon />
                </a>
              ) : (
                <Link className="aivis-docs-button" to="/ai-vis/settings">
                  Go to Data Studio Keys
                  <ArrowIcon />
                </Link>
              )}
              <a className="aivis-docs-button aivis-docs-button--secondary" href="#create-key">
                Start with your API key
              </a>
            </div>
            <ul className="aivis-docs-hero-facts" aria-label="Connector highlights">
              <li><CheckIcon /> One assigned key per project manager</li>
              <li><CheckIcon /> One client selection per data source</li>
              <li><CheckIcon /> Current and future AI-Vis clients</li>
            </ul>
          </div>
        </section>

        <div className="aivis-docs-container aivis-docs-layout">
          <aside className="aivis-docs-sidebar">
            <nav aria-label="On this page">
              <p>On this page</p>
              {sections.map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={activeSection === id ? 'aivis-docs-nav-link--active' : undefined}
                  aria-current={activeSection === id ? 'location' : undefined}
                >
                  {label}
                </a>
              ))}
            </nav>
            <div className="aivis-docs-sidebar-help">
              <strong>Need access?</strong>
              <span>Ask an AI-Vis administrator for a project-manager key.</span>
            </div>
          </aside>

          <article className="aivis-docs-content">
            <section id="overview" className="aivis-docs-section">
              <p className="aivis-docs-section-kicker">Overview</p>
              <h2>How the connection works</h2>
              <p>
                Your API key authenticates your Google account with the AI-Vis
                connector. The client is not locked to that key. Instead, Looker
                Studio saves a separate client selection inside every data source.
              </p>
              <ol className="aivis-docs-flow" aria-label="Connector workflow">
                <li><span>1</span><strong>Get a key</strong><small>Assigned to you by an administrator</small></li>
                <li><span>2</span><strong>Authenticate</strong><small>Enter it once in the connector</small></li>
                <li><span>3</span><strong>Select a client</strong><small>Stored in this data source only</small></li>
                <li><span>4</span><strong>Build charts</strong><small>Use AI-Vis dimensions and metrics</small></li>
              </ol>
              <div className="aivis-docs-callout">
                <div className="aivis-docs-callout-icon" aria-hidden>i</div>
                <div>
                  <strong>Credential and client selection are separate</strong>
                  <p>
                    The credential is stored for your Google user. The selected
                    Client is stored per data source, so changing one data source
                    never changes another report or client.
                  </p>
                </div>
              </div>
              <div className="aivis-docs-prerequisites">
                <h3>Before you begin</h3>
                <ul>
                  <li><CheckIcon /> A Google account with access to Looker Studio</li>
                  <li><CheckIcon /> An all-clients Data Studio key assigned to you</li>
                  <li><CheckIcon /> Permission to edit the target Looker Studio report</li>
                </ul>
              </div>
            </section>

            <section id="create-key" className="aivis-docs-section">
              <p className="aivis-docs-section-kicker">Step 1</p>
              <h2>Create and hand off a Data Studio key</h2>
              <p>
                This step is completed by an AI-Vis system or company admin. Create a
                separate key for each project manager so that access can be
                reviewed and revoked independently.
              </p>
              <StepList>
                <Step title="Open Data Studio Keys">
                  Sign in to AI-Vis, open <strong>Settings</strong>, then select
                  the <strong>Data Studio Keys</strong> tab.
                </Step>
                <Step title="Create a named key">
                  Select <strong>New Data Studio key</strong>. Use an owner-specific
                  name such as <code>Jeff - Looker Studio</code>, then select
                  <strong>Create key</strong>.
                </Step>
                <Step title="Copy and hand off the key securely">
                  Copy the complete key and share it using your approved password
                  manager or secret-sharing tool. Do not send keys in plain-text
                  email or chat.
                </Step>
              </StepList>
              <div className="aivis-docs-note">
                <strong>The key can be copied again.</strong>
                System admins can use <strong>Copy key</strong> from the Data
                Studio Keys table at any time. Every key can read reporting data
                for all current and future active clients.
              </div>
            </section>

            <section id="connect" className="aivis-docs-section">
              <p className="aivis-docs-section-kicker">Step 2</p>
              <h2>Authenticate and select a client</h2>
              <StepList>
                <Step title="Open the AI-Vis connector">
                  From AI-Vis Settings, select <strong>Open connector</strong>,
                  or use the connector button at the top of this guide.
                </Step>
                <Step title="Authorize the connector">
                  Choose the Google account that will own the data source and
                  approve the requested Apps Script access. If Google shows an
                  unverified-app notice for the private connector, select
                  <strong>Advanced</strong> and continue to AI-Vis.
                </Step>
                <Step title="Enter your assigned API key">
                  Paste the complete AI-Vis Data Studio key into the credential
                  field and select <strong>Submit</strong>. You normally do this
                  once for your Google account.
                </Step>
                <Step title="Choose the Client">
                  In connector configuration, open the required
                  <strong>Client</strong> menu and select the client this data
                  source should represent.
                </Step>
                <Step title="Add the data source">
                  Select <strong>Connect</strong>. Review the available fields,
                  then select <strong>Create Report</strong> for a new report or
                  <strong>Add to report</strong> for an existing report.
                </Step>
              </StepList>
              <div className="aivis-docs-warning">
                <strong>Do not reset authentication to switch clients.</strong>
                Add or edit a data source and select another Client. Reset the
                credential only when replacing a key or changing Google users.
              </div>
            </section>

            <section id="multiple-clients" className="aivis-docs-section">
              <p className="aivis-docs-section-kicker">Step 3</p>
              <h2>Use another client without replacing the credential</h2>
              <div className="aivis-docs-scenario">
                <h3>Add another client to the same report</h3>
                <ol>
                  <li>Open the report in <strong>Edit</strong> mode.</li>
                  <li>Select <strong>Add data</strong> in the toolbar.</li>
                  <li>Choose the AI-Vis connector or an AI-Vis data source.</li>
                  <li>Select the new <strong>Client</strong>, then connect it.</li>
                  <li>Assign the new data source to the charts for that client.</li>
                </ol>
              </div>
              <div className="aivis-docs-scenario">
                <h3>Use another client in a different report</h3>
                <ol>
                  <li>Open the AI-Vis connector again.</li>
                  <li>Select the Client for the new data source.</li>
                  <li>Select <strong>Connect</strong>, then <strong>Create Report</strong>.</li>
                  <li>Add the charts described below.</li>
                </ol>
              </div>
              <p className="aivis-docs-muted">
                Looker Studio may skip the credential screen because your Google
                user is already authenticated. That is expected. Every new data
                source still asks for its own Client.
              </p>
            </section>

            <section id="charts" className="aivis-docs-section">
              <p className="aivis-docs-section-kicker">Step 4</p>
              <h2>Build the three AI-Vis charts</h2>
              <p>
                Add each chart separately and make sure it uses the intended
                AI-Vis data source. Visibility and Source Usage are already
                provided as percentage fields.
              </p>
              <div className="aivis-docs-recipes">
                <Recipe
                  number="01"
                  title="Visibility Score for Test Prompts"
                  description="Ranks the client and tracked competitors by AI visibility."
                >
                  <Field label="Chart type" value="Table" />
                  <Field label="Dimension" value="Brand" />
                  <Field label="Metric" value="Visibility" />
                  <Field label="Sort" value="Visibility" note="Descending" />
                </Recipe>
                <Recipe
                  number="02"
                  title="Top Domains for Test Prompts"
                  description="Shows the domains most frequently retrieved as AI sources."
                >
                  <Field label="Chart type" value="Table" />
                  <Field label="Dimension" value="Domain" />
                  <Field label="Metric" value="Source Usage" />
                  <Field label="Sort" value="Source Usage" note="Descending" />
                  <Field label="Optional fields" value="Citations, Domain Classification" />
                </Recipe>
                <Recipe
                  number="03"
                  title="Visibility Trend by Brand"
                  description="Compares visibility across completed campaign-run dates."
                >
                  <Field label="Chart type" value="Time series" />
                  <Field label="Date dimension" value="Date" />
                  <Field label="Breakdown dimension" value="Brand" />
                  <Field label="Metric" value="Visibility" />
                  <Field label="Granularity" value="Day" />
                  <Field label="Date range" value="Last 30 days" note="Or a custom period containing campaign runs" />
                </Recipe>
              </div>
              <div className="aivis-docs-warning">
                <strong>Keep brand and domain fields in separate charts.</strong>
                Brand and Visibility use the brands report. Domain, Domain
                Classification, Citations, and Source Usage use the domains
                report. Looker Studio will show an error if both groups are used
                in the same chart.
              </div>
            </section>

            <section id="dates-refresh" className="aivis-docs-section">
              <p className="aivis-docs-section-kicker">Data behavior</p>
              <h2>Date ranges and refreshes</h2>
              <ul className="aivis-docs-detail-list">
                <li>
                  <strong>Report date controls are supported.</strong>
                  The connector sends the selected chart or report date range to
                  AI-Vis. Without one, the API defaults to the last 30 days.
                </li>
                <li>
                  <strong>Only actual run dates are returned.</strong>
                  The connector does not create zero-value rows for dates when
                  the campaign did not run.
                </li>
                <li>
                  <strong>Completed runs update reporting automatically.</strong>
                  Refresh the data source or report after a campaign run finishes
                  if Looker Studio is still showing cached results.
                </li>
                <li>
                  <strong>Historical data depends on reporting rollups.</strong>
                  If old completed runs are missing, an AI-Vis maintainer may need
                  to rebuild historical daily rollups once.
                </li>
              </ul>
            </section>

            <section id="troubleshooting" className="aivis-docs-section">
              <p className="aivis-docs-section-kicker">Reference</p>
              <h2>Troubleshooting</h2>
              <div className="aivis-docs-troubleshooting">
                <details>
                  <summary>The API key is invalid or authentication fails</summary>
                  <p>
                    Copy the complete key again from AI-Vis and remove any extra
                    spaces. Confirm the key has not been revoked and its creator
                    is still an active system or company admin.
                  </p>
                </details>
                <details>
                  <summary>No clients appear in the Client menu</summary>
                  <p>
                    Confirm you are using a current all-clients Data Studio key,
                    not an automation key. The menu contains active, non-archived
                    AI-Vis clients.
                  </p>
                </details>
                <details>
                  <summary>A chart says “No data”</summary>
                  <p>
                    Confirm the chart is using the correct client data source and
                    that its date range includes completed campaign runs. A client
                    with no completed run in that period correctly returns no rows.
                  </p>
                </details>
                <details>
                  <summary>The wrong client appears in a report</summary>
                  <p>
                    Edit that report's data source connection and change the
                    Client. Do not reset the shared connector credential.
                  </p>
                </details>
                <details>
                  <summary>Brand and domain fields cannot be used together</summary>
                  <p>
                    Use separate charts. Build visibility charts with Brand and
                    Visibility. Build citation charts with Domain, Source Usage,
                    Citations, and Domain Classification.
                  </p>
                </details>
                <details>
                  <summary>A revoked key is still configured in Looker Studio</summary>
                  <p>
                    Reset the AI-Vis connector authentication, enter the
                    replacement project-manager key, then verify the Client
                    selection on every existing AI-Vis data source.
                  </p>
                </details>
              </div>
            </section>

            <footer className="aivis-docs-footer">
              <div>
                <ConnectorIcon size={22} />
                <div>
                  <strong>Ready to build the report?</strong>
                  <span>Open the connector and select your first client.</span>
                </div>
              </div>
              {CONNECTOR_URL ? (
                <a
                  className="aivis-docs-button"
                  href={CONNECTOR_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Open connector
                  <ExternalLinkIcon />
                </a>
              ) : (
                <Link className="aivis-docs-button" to="/ai-vis/settings">
                  Go to AI-Vis settings
                  <ArrowIcon />
                </Link>
              )}
            </footer>
          </article>
        </div>
      </main>
    </div>
  );
}
